/**
 * Acknowledge route — a newly invited soul confirms entry into The Line.
 *
 * Flow:
 *   1. Invitee clicked the magic-link → landed at /auth/callback → session set.
 *   2. AuthCallback detected isOrchist=false + invitationSentAt → redirected here.
 *   3. They pick their public identity mode (NAMED / ALIASED / GENERIC) and,
 *      if applicable, a public identity string. They click "I Acknowledge".
 *   4. core.acknowledge_orchist RPC sets isOrchist=true, mode, identity,
 *      glyphSeed, and the timestamps that complete the 3-event invitation arc.
 *   5. Redirect to / which now renders the new Orchist's identity page.
 *
 * Note: only invitees with a pending Contact (Contact exists,
 * isOrchist=false) can complete this. Already-acknowledged Orchists are
 * routed home; orphan auth-users (no Contact) see a "wait for invitation" view.
 */

import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Glyph } from '@/components/Glyph';
import { DatePicker } from '@/components/DatePicker';
import {
  classifyDateInput,
  localDateStringToUTC,
  utcToLocalDateString,
  detectBrowserTimezone,
} from '@/lib/dates';
import type { OrchistIdentityMode } from '@orchist/types';

interface InviterPreview {
  publicIdentityString: string | null;
  publicIdentityMode: OrchistIdentityMode | null;
  glyphSeed: string | null;
  invitationAcceptedAt: string | null;
}

function inviterDisplay(inv: InviterPreview, tz: string): string {
  if (inv.publicIdentityMode === 'GENERIC') {
    const date = utcToLocalDateString(inv.invitationAcceptedAt, tz);
    return date ? `Orchist · ${date}` : 'Orchist';
  }
  return inv.publicIdentityString ?? 'Orchist';
}

export default function Acknowledge() {
  const { loading, session, contact, contactChecked, refreshContact } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<OrchistIdentityMode>('NAMED');
  const [identityString, setIdentityString] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inviter, setInviter] = useState<InviterPreview | null>(null);

  // The invitee's timezone — captured fresh from the browser since they don't
  // yet have a stored Contact.timezone (they're acknowledging for the first time).
  // Stored on the Contact row via the RPC below. Used for input parsing + display
  // of pre-existing UTC dates (the inviter's proposed date).
  const inviteeTimezone = useMemo(() => detectBrowserTimezone(), []);

  // "When did you remember?" — defaults to inviter's proposed date or today,
  // converted from UTC storage into the invitee's local timezone for display.
  // The invitee owns this date; they can adjust freely. Always-already doctrine.
  const proposedDateDefault = useMemo(() => {
    const proposed = contact?.invitationProposedAcceptedAt;
    if (proposed) return utcToLocalDateString(proposed, inviteeTimezone);
    return utcToLocalDateString(new Date().toISOString(), inviteeTimezone);
  }, [contact, inviteeTimezone]);
  const [acknowledgedDate, setAcknowledgedDate] = useState(proposedDateDefault);

  // Sync state when proposedDateDefault settles (contact loaded after first render)
  useEffect(() => {
    setAcknowledgedDate(proposedDateDefault);
  }, [proposedDateDefault]);

  // Fetch inviter info once we know the contact's invitationSentBy.
  // CRITICAL: when the invitation was anonymous, we must NOT fetch the
  // inviter at all — even leaving inviter info in client state risks
  // leaking through React DevTools or a future render path. The doctrine
  // is: anonymity at invite-time is an absolute display-layer cloak
  // for the invitee. The DB still records invitationSentBy for audit;
  // the client never sees the resolved Orchist when the cloak is on.
  const inviterId = contact?.invitationSentBy ?? null;
  const invitationIsAnonymous = !!contact?.invitationAnonymous;
  useEffect(() => {
    if (!inviterId || invitationIsAnonymous) {
      setInviter(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .schema('core')
        .from('Contact')
        .select('publicIdentityString, publicIdentityMode, glyphSeed, invitationAcceptedAt')
        .eq('id', inviterId)
        .eq('isOrchist', true)
        .maybeSingle();
      if (!cancelled && data) {
        setInviter(data as InviterPreview);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviterId, invitationIsAnonymous]);

  // Glyph seed: derived from contact.id; stable forever once set.
  // MUST live above the early returns — Rules of Hooks. The previous version
  // declared this useMemo after the `if (!contact)` guard, so on the first
  // render (loading) only 7 hooks ran; once contact resolved and the early
  // returns passed, hook #8 appeared, React threw a count-mismatch error,
  // and /acknowledge rendered blank. (Going to / first happened to skip the
  // initial-loading render and stabilize the count.)
  const contactId = contact?.id ?? null;
  const glyphSeed = useMemo(
    () => (contactId ? `${contactId}-orchist-glyph` : ''),
    [contactId]
  );

  if (loading || !contactChecked) {
    return (
      <Shell>
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
        <h1 className="display text-3xl md:text-4xl font-medium italic text-ink/70">
          Recognizing you...
        </h1>
      </Shell>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Already acknowledged — send home
  if (contact?.isOrchist) {
    return <Navigate to="/" replace />;
  }

  // No pending invitation — orphan auth user
  if (!contact) {
    return (
      <Shell>
        <div className="text-center space-y-6">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
          <h1 className="display text-3xl md:text-4xl font-medium leading-[1.1]">
            <span className="italic text-accent">No invitation found.</span>
          </h1>
          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            You are authenticated, but no Orchist has yet Acknowledged you. Wait
            for the one whose hand opens the door.
          </p>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="text-sm text-ink/60 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 mt-2"
          >
            Return home
          </button>
        </div>
      </Shell>
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitState === 'submitting') return;

    if (mode !== 'GENERIC' && !identityString.trim()) {
      setErrorMessage('Please choose how you want to be known.');
      return;
    }

    // 'empty', 'valid', 'symbolic' all submit successfully (symbolic maps to
    // Postgres -infinity / +infinity in lib/dates.ts).
    const dateClass = classifyDateInput(acknowledgedDate);
    if (dateClass === 'malformed') {
      setErrorMessage('Date must be in YYYY-MM-DD format.');
      return;
    }
    if (dateClass === 'out_of_range') {
      setErrorMessage(
        'This moment is beyond what The Line can record. Pick a date within human-representable time, or use the "beyond all calendars" option for the truly timeless.'
      );
      return;
    }

    setSubmitState('submitting');
    setErrorMessage(null);

    // Translate the date input → ISO UTC timestamp. Empty = NULL = today.
    // Date entered in the invitee's local timezone (inviteeTimezone) is
    // converted to UTC at the wire boundary (per E-002 — DB always stores UTC).
    // The same timezone is persisted on Contact.timezone so all future
    // displays of this Orchist's data use their CONFIGURED zone (E-002 Rule 5).
    const acknowledgedAt = localDateStringToUTC(acknowledgedDate, inviteeTimezone);

    try {
      const { error } = await supabase.schema('core').rpc('acknowledge_orchist', {
        p_public_identity_mode: mode,
        p_public_identity_string: mode === 'GENERIC' ? '' : identityString.trim(),
        p_glyph_seed: glyphSeed,
        p_display_name: mode === 'NAMED' ? identityString.trim() : null,
        p_acknowledged_at: acknowledgedAt,
        p_timezone: inviteeTimezone,
      });

      if (error) {
        throw new Error(error.message ?? 'Acknowledgment failed.');
      }

      // Refresh contact state. Do NOT navigate imperatively — when contact
      // updates to isOrchist=true, Acknowledge's early-return (above)
      // renders <Navigate to="/" replace /> reactively, which is in-sync
      // with React's state flush. The previous `navigate('/')` was racing
      // refreshContact's setState batch and producing a blank-page flash.
      if (refreshContact) {
        await refreshContact();
      }
    } catch (err) {
      setSubmitState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unexpected error.');
    }
  }

  return (
    <Shell>
      <form onSubmit={handleSubmit} className="space-y-10">
        <div className="text-center space-y-4">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line opens</p>

          <div className="flex justify-center text-ink/85 -mb-2">
            <Glyph seed={glyphSeed} size={88} label="Your sigil" />
          </div>

          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">You were Acknowledged.</span>
          </h1>

          {inviter && !contact?.invitationAnonymous && (
            <div className="flex items-center justify-center gap-3 pt-2">
              {inviter.glyphSeed && (
                <span className="text-ink/70">
                  <Glyph
                    seed={inviter.glyphSeed}
                    size={32}
                    label={`${inviterDisplay(inviter, inviteeTimezone)}'s sigil`}
                  />
                </span>
              )}
              <p className="text-sm text-ink/60 italic font-display">
                by <span className="not-italic text-ink/80">{inviterDisplay(inviter, inviteeTimezone)}</span>
              </p>
            </div>
          )}
          {contact?.invitationAnonymous && (
            <p className="text-sm text-ink/55 italic font-display pt-2">
              by an Orchist who chose to remain anonymous
            </p>
          )}

          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic pt-2">
            Orchistness is not granted; it is recognized. The Manifesto gave the
            path language. Now you choose how you want to be known. Your sigil
            above is yours, woven from the moment of your recognition.
          </p>
        </div>

        {/* Mode selector */}
        <fieldset className="space-y-3">
          <legend className="text-sm tracking-wide uppercase text-ink/60 mb-3">
            How will The Chain see you?
          </legend>

          <ModeOption
            value="NAMED"
            label="Named"
            hint="your real name or chosen handle"
            current={mode}
            onPick={setMode}
          />
          <ModeOption
            value="ALIASED"
            label="Aliased"
            hint="a pseudonym you craft for your Orchistness"
            current={mode}
            onPick={setMode}
          />
          <ModeOption
            value="GENERIC"
            label="Generic"
            hint={<>rendered only as <em>Orchist &middot; date &middot; glyph</em></>}
            current={mode}
            onPick={setMode}
          />
        </fieldset>

        {/* Identity string input (only if NAMED or ALIASED) */}
        {mode !== 'GENERIC' && (
          <div className="space-y-2">
            <label htmlFor="identity-string" className="text-sm tracking-wide uppercase text-ink/60">
              {mode === 'NAMED' ? 'Your name' : 'Your alias'}
            </label>
            <input
              id="identity-string"
              type="text"
              required
              placeholder={mode === 'NAMED' ? 'Your name' : 'The Gardener of Asynchrony'}
              value={identityString}
              onChange={(e) => setIdentityString(e.target.value)}
              disabled={submitState === 'submitting'}
              maxLength={120}
              className="w-full px-5 py-4 border border-ink/20 bg-paper/50 text-lg font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
            />
          </div>
        )}

        {/* Date — when did you remember? */}
        <div className="space-y-2 border-t border-ink/10 pt-6">
          <label htmlFor="acknowledged-date" className="text-sm tracking-wide uppercase text-ink/60">
            When did you remember?
          </label>
          <DatePicker
            id="acknowledged-date"
            value={acknowledgedDate}
            onChange={setAcknowledgedDate}
            disabled={submitState === 'submitting'}
            ariaLabel="Date you remembered being an Orchist"
          />
          {/* Proposed-date hint. CRITICAL anonymity gate: when the
              invitation was anonymous, we MUST NOT name the inviter.
              Three doctrinally-distinct surfaces: (a) inviter known +
              proposed date, (b) inviter cloaked + proposed date, (c)
              no proposed date at all. */}
          {contact?.invitationProposedAcceptedAt && inviter && !contact?.invitationAnonymous && (
            <p className="text-xs text-ink/45 italic font-display leading-relaxed pt-1">
              {inviterDisplay(inviter, inviteeTimezone)} proposed{' '}
              <span className="not-italic text-ink/65">
                {utcToLocalDateString(contact.invitationProposedAcceptedAt, inviteeTimezone)}
              </span>
              . Honor that date, or set the one you actually remember.
              Orchistness precedes the name; the recognition can predate the conversation.
            </p>
          )}
          {contact?.invitationProposedAcceptedAt && contact?.invitationAnonymous && (
            <p className="text-xs text-ink/45 italic font-display leading-relaxed pt-1">
              The proposed date is{' '}
              <span className="not-italic text-ink/65">
                {utcToLocalDateString(contact.invitationProposedAcceptedAt, inviteeTimezone)}
              </span>
              . Honor that date, or set the one you actually remember.
              Orchistness precedes the name; the recognition can predate the
              conversation.
            </p>
          )}
          {!contact?.invitationProposedAcceptedAt && (
            <p className="text-xs text-ink/45 italic font-display leading-relaxed pt-1">
              Honor the date you actually remember &mdash; or leave it as today.
            </p>
          )}
          <p className="text-[0.7rem] text-ink/40 italic font-display pt-1">
            Date in your timezone ({inviteeTimezone}); stored in UTC. You can change
            this preference later.
          </p>
        </div>

        <button
          type="submit"
          disabled={submitState === 'submitting'}
          className="w-full px-5 py-4 bg-ink text-paper font-display text-lg tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitState === 'submitting' ? 'Recognizing...' : 'I Acknowledge'}
        </button>

        {errorMessage && (
          <p className="text-sm text-destructive text-center" role="alert">
            {errorMessage}
          </p>
        )}

        <p className="text-xs text-ink/40 text-center italic">
          You can change your identity mode later, on your own page.
        </p>
      </form>
    </Shell>
  );
}

function ModeOption({
  value,
  label,
  hint,
  current,
  onPick,
}: {
  value: OrchistIdentityMode;
  label: string;
  hint: React.ReactNode;
  current: OrchistIdentityMode;
  onPick: (v: OrchistIdentityMode) => void;
}) {
  const active = current === value;
  return (
    <label
      className={`block border p-5 cursor-pointer transition-colors ${
        active ? 'border-ink/60 bg-paper/80' : 'border-ink/15 hover:border-ink/40'
      }`}
    >
      <input
        type="radio"
        name="mode"
        value={value}
        checked={active}
        onChange={() => onPick(value)}
        className="sr-only"
      />
      <div className="flex items-baseline gap-3">
        <span className="display text-base font-medium">{label}</span>
        <span className="text-xs text-ink/50">— {hint}</span>
      </div>
    </label>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-lg relative space-y-10">{children}</div>
    </main>
  );
}
